/**
 * Match pathname against admin-configured hide-nav patterns.
 * Supports exact paths and * wildcards (e.g. /shop/... or /casting/.../apply).
 */

export function normalizeNavPath(raw: string): string {
  let s = (raw || '').trim();
  if (!s) return '/';
  // Drop query/hash if present
  const withoutQuery = s.split('?')[0] || s;
  s = withoutQuery.split('#')[0] || withoutQuery;
  if (!s.startsWith('/')) s = '/' + s;
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s || '/';
}

/** Normalize a user-entered pattern for storage / display. */
export function normalizeNavHidePattern(raw: string): string {
  let s = (raw || '').trim();
  if (!s) return '';
  const withoutQuery = s.split('?')[0] || s;
  s = withoutQuery.split('#')[0] || withoutQuery;
  if (!s.startsWith('/') && !s.startsWith('*')) s = '/' + s;
  // Keep trailing star-slash; strip other trailing slashes
  if (s.length > 1 && s.endsWith('/') && !s.endsWith('/*')) s = s.slice(0, -1);
  return s;
}

/**
 * True when pathname matches pattern.
 * Exact: /contact
 * Trailing wildcard (/shop + star-slash): matches /shop and /shop/...
 * General *: any run of characters (including /)
 */
export function pathMatchesNavHidePattern(pathname: string, pattern: string): boolean {
  const path = normalizeNavPath(pathname);
  const pat = normalizeNavHidePattern(pattern);
  if (!pat) return false;

  // Trailing star-slash → prefix match (include the base path itself).
  if (pat.endsWith('/*')) {
    const base = pat.slice(0, -2);
    const baseNorm = normalizeNavPath(base);
    return path === baseNorm || path.startsWith(baseNorm + '/');
  }

  // Exact (no wildcards)
  if (pat.indexOf('*') === -1) {
    return path === normalizeNavPath(pat);
  }

  // General glob: escape regex specials, then * → .*
  const escaped = pat.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  try {
    return new RegExp('^' + escaped + '$', 'i').test(path);
  } catch (e) {
    return false;
  }
}

export function pathMatchesAnyNavHidePattern(
  pathname: string,
  patterns: string[] | undefined | null,
): boolean {
  if (!Array.isArray(patterns) || patterns.length === 0) return false;
  return patterns.some((p) => pathMatchesNavHidePattern(pathname, p));
}

/** Suggested starters for the admin chip UI. */
export const NAV_HIDE_PATH_PATTERN_SUGGESTIONS: { pattern: string; label: string }[] = [
  { pattern: '/shop/checkout*', label: 'Checkout' },
  { pattern: '/shop/tickets*', label: 'Tickets' },
  { pattern: '/casting/*', label: 'Casting' },
  { pattern: '/event-inquiry/*', label: 'Event inquiry' },
  { pattern: '/members*', label: 'Members' },
];
